/**
 * Drawing Operations - Funktioner för att hantera drawing tools och house marker
 *
 * Använder closure för att hålla state för Terra Draw och house marker
 */

import {
    TerraDraw,
    TerraDrawPolygonMode,
    TerraDrawCircleMode,
    TerraDrawRenderMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import * as turf from '../utils/turf';
import CustomMarker from '../ui/custom-marker';
import * as mapState from '../map/map-state';
import * as districtCache from '../districts/district-cache';

/** Matchar tidigare google.maps.drawing polygonOptions/circleOptions (map-config DRAW_OPTIONS). */
const DISTRICT_DRAW_STROKE = {
    fillColor: '#57c9dc',
    fillOpacity: 0.3,
    outlineColor: '#57c9dc',
    outlineOpacity: 1,
    outlineWidth: 2
};

// Closure state
let terraDrawInstance = null;
let clickHouseListeners = [];

function whenMapProjectionReady(map, maps, onReady) {
    if (map.getProjection()) {
        onReady();
    } else {
        maps.event.addListenerOnce(map, 'projection_changed', onReady);
    }
}

function createDistrictTerraDrawModes() {
    const polygonMode = new TerraDrawPolygonMode({
        editable: true,
        styles: {
            ...DISTRICT_DRAW_STROKE,
            closingPointColor: '#57c9dc',
            closingPointOpacity: 1,
            closingPointOutlineColor: '#57c9dc',
            closingPointOutlineWidth: 2,
            closingPointOutlineOpacity: 1,
            coordinatePointColor: '#57c9dc',
            coordinatePointOpacity: 1,
            coordinatePointOutlineColor: '#57c9dc',
            coordinatePointOutlineWidth: 2,
            coordinatePointOutlineOpacity: 1,
            editedPointColor: '#57c9dc',
            editedPointOpacity: 1,
            editedPointOutlineColor: '#57c9dc',
            editedPointOutlineWidth: 2,
            editedPointOutlineOpacity: 1
        }
    });

    const circleMode = new TerraDrawCircleMode({
        styles: { ...DISTRICT_DRAW_STROKE }
    });

    const idleMode = new TerraDrawRenderMode({
        modeName: 'idle',
        styles: {
            polygonFillColor: '#57c9dc',
            polygonFillOpacity: 0,
            polygonOutlineColor: '#57c9dc',
            polygonOutlineWidth: 0,
            pointColor: '#57c9dc',
            pointWidth: 0,
            pointOpacity: 0,
            pointOutlineColor: '#57c9dc',
            pointOutlineOpacity: 0,
            pointOutlineWidth: 0,
            lineStringWidth: 0,
            lineStringColor: '#57c9dc',
            lineStringOpacity: 0,
            zIndex: 0
        },
        pointerEvents: {
            leftClick: false,
            rightClick: false,
            contextMenu: false,
            onDragStart: false,
            onDrag: false,
            onDragEnd: false
        }
    });

    return { polygonMode, circleMode, idleMode };
}

/**
 * Initialize Terra Draw när Google Maps API är laddad
 * @param {Object} params - { map, maps, selectAreasBatch }
 * @param {Function} params.selectAreasBatch - Batch callback that receives an array of features
 */
export function initializeDrawingManager({ map, maps, selectAreasBatch }) {
    return new Promise((resolve) => {
        whenMapProjectionReady(map, maps, () => {
            const { polygonMode, circleMode, idleMode } = createDistrictTerraDrawModes();

            const adapter = new TerraDrawGoogleMapsAdapter({
                lib: maps,
                map,
                coordinatePrecision: 9
            });

            const draw = new TerraDraw({
                adapter,
                modes: [polygonMode, circleMode, idleMode]
            });

            const onFinish = (id, context) => {
                if (context.action !== 'draw') {
                    return;
                }
                const feature = draw.getSnapshotFeature(id);
                if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
                    handleDrawingCompleteFromPolygon(
                        turf.polygon(feature.geometry.coordinates),
                        selectAreasBatch
                    );
                }
                if (draw.hasFeature(id)) {
                    draw.removeFeatures([id]);
                }
                draw.setMode('idle');
                mapState.setAreaClickEnabled(true);
                const tools = mapState.getMapState().tools;
                if (tools && tools.resetTools) {
                    tools.resetTools();
                }
            };

            draw.on('finish', onFinish);
            draw.start();
            draw.on('ready', () => {
                draw.setMode('idle');
                resolve();
            });
            terraDrawInstance = draw;
        });
    });
}

/**
 * Hantera när drawing är klar.
 * Samlar alla intersecting features och anropar selectAreasBatch en gång.
 * @param {Object} overlayPolygon - turf Polygon feature
 * @param {Function} selectAreasBatch - Batch callback (features[])
 */
function handleDrawingCompleteFromPolygon(overlayPolygon, selectAreasBatch) {
    const selectionType = districtCache.getCurrentDistrictSelectionType();
    const turfPolygons = mapState.getTurfPolygons(selectionType);

    if (!turfPolygons) {
        return;
    }

    const overlayBbox = computeBbox(overlayPolygon);

    const matchedFeatures = [];
    turfPolygons.forEach(data => {
        if (data.feature.properties.type !== selectionType) return;

        if (!data._bbox) {
            data._bbox = computeBbox(data.polygon);
        }
        if (!bboxOverlap(overlayBbox, data._bbox)) return;

        if (turf.intersect(overlayPolygon, data.polygon)) {
            matchedFeatures.push(data.feature);
        }
    });

    if (matchedFeatures.length > 0) {
        selectAreasBatch(matchedFeatures);
    }
}

function computeBbox(polygon) {
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    const coords = polygon.geometry.coordinates;
    const rings = polygon.geometry.type === 'MultiPolygon'
        ? coords.flatMap(poly => poly)
        : coords;
    for (const ring of rings) {
        for (const [lng, lat] of ring) {
            if (lng < minLng) minLng = lng;
            if (lng > maxLng) maxLng = lng;
            if (lat < minLat) minLat = lat;
            if (lat > maxLat) maxLat = lat;
        }
    }
    return [minLng, minLat, maxLng, maxLat];
}

function bboxOverlap(a, b) {
    return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

/**
 * Starta polygon drawing mode
 */
export function drawPolygon() {
    if (!terraDrawInstance) {
        console.warn('Terra Draw not initialized');
        return;
    }
    mapState.setAreaClickEnabled(false);
    terraDrawInstance.setMode('polygon');
}

/**
 * Starta circle drawing mode
 */
export function drawCircle() {
    if (!terraDrawInstance) {
        console.warn('Terra Draw not initialized');
        return;
    }
    mapState.setAreaClickEnabled(false);
    terraDrawInstance.setMode('circle');
}

/**
 * Stoppa drawing mode och återgå till idle
 */
export function stopDrawing() {
    if (!terraDrawInstance) {
        return;
    }
    terraDrawInstance.setMode('idle');
    mapState.setAreaClickEnabled(true);
}

/**
 * Skapa koordinater för en cirkel
 * @param {Object} center - Center point (LatLng)
 * @param {number} radius - Radius i meter
 * @param {number} points - Antal punkter (default: 360)
 * @returns {Array} Array av LatLng points
 */
export function createCoordinatesCircle(center, radius, points = 360) {
    const maps = mapState.getMaps();
    let a = [], p = 360/points, d = 0;
    for(var i = 0; i < points; ++i, d += p) {
        a.push(maps.geometry.spherical.computeOffset(center, radius, d));
    }
    return a;
}

/**
 * Konvertera Google Maps paths till koordinater
 * @param {Array} paths - Array av LatLng points
 * @returns {Array} Array av [lng, lat] koordinater
 */
export function convertGoogleMapsPathsToCoordinates(paths) {
    return [...paths.map(coord => {
        return [coord.lng(), coord.lat()];
    }), [paths[0].lng(), paths[0].lat()]];
}

/**
 * Skapa en house marker med gemensam logik
 * @param {Object} options - Konfigurationsobjekt
 * @param {Object} options.maps - Google Maps API
 * @param {Object} options.map - Google Maps instance
 * @param {Object} options.center - Center point (LatLng)
 * @param {boolean} options.draggable - Om marker ska vara draggable (default: false)
 * @param {Function} options.onMarkerMoved - Callback när marker flyttas (optional)
 * @param {Function} options.onClose - Callback när close button klickas (optional)
 * @param {boolean} options.showCloseButton - Om close button ska visas (default: true)
 * @returns {Object} Marker instance
 */
export function createHouseMarker({
    maps,
    map,
    center,
    draggable = false,
    onMarkerMoved = null,
    onClose = null,
    showCloseButton = true,
    style = {}
}) {
    // Create the marker HTML
    const markerDiv = document.createElement('div');
    markerDiv.className = 'custom-marker-house';

    // Apply styles correctly - style is a CSSStyleDeclaration object, not a regular object
    Object.assign(markerDiv.style, style, { zIndex: '4' });
    markerDiv.innerHTML = `
        <div class="custom-marker-house__icon"></div>
        ${showCloseButton ? '<button class="custom-marker-house__close">×</button>' : ''}
    `;

    // Create the custom marker
    const marker = CustomMarker({
        maps,
        map,
        latlng: center,
        draggable,
        appendNode: markerDiv,
        onMarkerMoved: onMarkerMoved || undefined
    });

    // Add click handler for the close button
    if (showCloseButton) {
        const closeButton = markerDiv.querySelector('.custom-marker-house__close');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => {
                e.stopPropagation();
                marker.setMap(null);
                if (onClose) {
                    onClose(null);
                }
            });
        }
    }

    // Set the marker on the map
    marker.setMap(map);

    return marker;
}

/**
 * Rendera house marker på kartan
 * @param {Object} center - Center point (LatLng)
 * @param {boolean} draggable - Om marker ska vara draggable
 * @returns {Object} Marker instance
 */
export function renderHouseMarker(center, draggable = false, houseMarker = null, onChange = null) {
    const maps = mapState.getMaps();
    const map = mapState.getMap();

    // First remove any existing marker
    if (houseMarker) {
        houseMarker.setMap(null);
    }

    const currentDistrict = districtCache.getCurrent();
    const marker = createHouseMarker({
        maps,
        map,
        center,
        draggable,
        onMarkerMoved: (m) => {
            if (onChange) {
                onChange(m);
            }
        },
        onClose: () => {
            if (onChange) {
                onChange(null);
            }
        },
        style: {
            backgroundColor: currentDistrict?.color
        }
    });

    if (onChange) {
        onChange(marker);
    }

    return marker;
}

/**
 * Toggle draw house tool
 * @param {boolean} isToolActive - Om tool är aktiv
 */
export function toggleDrawHouse(isToolActive, houseMarker = null, onChange = null) {
    const map = mapState.getMap();
    const maps = mapState.getMaps();

    if (isToolActive) {
        if (houseMarker) {
            houseMarker.setDraggable(true);
        }

        mapState.setAreaClickEnabled(false);

        const clickListener = map.addListener('click', (event) => {
            const center = event.latLng;
            renderHouseMarker(center, true, houseMarker, onChange);
        });
        clickHouseListeners.push(clickListener);

    } else {
        clickHouseListeners.forEach(evt => {
            maps.event.removeListener(evt);
        });
        clickHouseListeners = [];

        if (houseMarker) {
            houseMarker.setDraggable(false);
        }

        mapState.setAreaClickEnabled(true);
    }
}

/**
 * Konvertera point till LatLng
 * @param {Array} point - [lng, lat] array
 * @returns {Object} LatLng instance
 */
export function toLatLng(point) {
    const maps = mapState.getMaps();
    return new maps.LatLng(point[1], point[0]);
}
